import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { Spinner } from "reactstrap";
import {
  richTextEditorBold,
  richTextEditorCenterAlign,
  richTextEditorItalic,
  richTextEditorLeftAlign,
  richTextEditorLink,
  richTextEditorOrderedList,
  richTextEditorRightAlign,
  richTextEditorUnderline,
  richTextEditorUnorderedList,
} from "../common/SvgIcons";

import "../styles/_customRichTextEditorComponent.scss";

const hasContent = (htmlString: string) => {
  if (!htmlString) return false;
  // Remove HTML tags and check if there's actual content
  const textContent = htmlString.replace(/<[^>]*>/g, "").trim();
  return textContent.length > 0;
};

const extensions = [
  StarterKit.configure({
    // Configure list items to work properly
    listItem: {
      HTMLAttributes: {
        class: "rich-text-list-item",
      },
    },
    bulletList: {
      HTMLAttributes: {
        class: "rich-text-bullet-list",
      },
    },
    orderedList: {
      HTMLAttributes: {
        class: "rich-text-ordered-list",
      },
    },
  }),
  TextAlign.configure({
    // Apply alignment to lists as well so the marker follows the text
    types: ["heading", "paragraph", "listItem"],
    alignments: ["left", "center", "right"],
  }),
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      class: "rich-text-link",
    },
  }),
  Underline,
];

const RichTextEditor = ({ content, onChange, resetFocus }: any) => {
  const [showLinkInput, setShowLinkInput] = useState(false); // Back to normal
  const [linkUrl, setLinkUrl] = useState("");
  const [linkPosition, setLinkPosition] = useState<any>({ top: 0, left: 0 });
  const [isFocused, setIsFocused] = useState(false);
  const richTextContainerRef = useRef<any>(null);
  const inputContainerRef = useRef<any>(null);
  const linkPopupRef = useRef<any>(null);
  const linkButtonRef = useRef<any>(null);
  const [savedSelection, setSavedSelection] = useState<any>(null); // To store selection before opening link input

  const editor = useEditor({
    extensions,
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    onCreate: ({ editor }) => {
      // Set focus with a small delay to ensure proper initialization
      setTimeout(() => {
        editor?.commands.focus();
      }, 100);
    },
    onFocus: () => {
      setIsFocused(true);
    },
    onBlur: () => {
      // Small delay to prevent immediate blur when clicking toolbar buttons
      setTimeout(() => setIsFocused(false), 100);
    },
  });

  const saveLink = useCallback(
    (e: any) => {
      e.preventDefault();
      if (!editor) return;

      // Restore the saved selection first
      if (savedSelection) {
        editor.view.dispatch(editor.view.state.tr.setSelection(savedSelection));
      }

      if (linkUrl) {
        const formattedUrl = linkUrl.startsWith("http://") || linkUrl.startsWith("https://") ? linkUrl : `https://${linkUrl}`;
        editor.chain().focus().extendMarkRange("link").setLink({ href: formattedUrl }).run();
      } else {
        editor.chain().focus().unsetLink().run();
      }

      setShowLinkInput(false);
      setLinkUrl("");
      setSavedSelection(null); // Clear saved selection
    },
    [editor, linkUrl, savedSelection]
  );

  const showLinkInputBox = useCallback(() => {
    console.log("showLinkInputBox called!"); // Debug log
    if (!editor) {
      console.log("No editor found");
      return;
    }

    // Save the current selection BEFORE it gets lost
    const currentSelection = editor.state.selection;
    setSavedSelection(currentSelection);
    console.log("Saved selection:", currentSelection); // Debug log

    // Get the current link URL if cursor is on a link
    const previousUrl = editor.getAttributes("link").href || "";
    setLinkUrl(previousUrl);
    console.log("Previous URL:", previousUrl); // Debug log

    // Calculate position based on the current selection
    const containerRect = inputContainerRef.current?.getBoundingClientRect();
    let popupX = 50;
    let popupY = 50;

    if (containerRect) {
      try {
        // Check if there's a text selection
        const { from, to } = currentSelection;
        const hasSelection = from !== to;
        console.log("Has selection:", hasSelection); // Debug log

        if (hasSelection) {
          // Use TipTap's coordinate system
          const coords = editor.view.coordsAtPos(from);
          const endCoords = editor.view.coordsAtPos(to);

          // Calculate position relative to container
          popupX = coords.left - containerRect.left + (inputContainerRef.current?.scrollLeft || 0);
          popupY = endCoords.bottom - containerRect.top + (inputContainerRef.current?.scrollTop || 0) + 5;

          // Ensure popup stays within bounds
          const popupWidth = 323;
          const popupHeight = 120;

          if (popupX + popupWidth > containerRect.width) {
            popupX = Math.max(5, containerRect.width - popupWidth - 10);
          }

          if (popupY + popupHeight > containerRect.height) {
            popupY = Math.max(5, containerRect.height - popupHeight - 10);
          }

          popupX = Math.max(5, popupX);
          popupY = Math.max(5, popupY);
        }
      } catch (error) {
        console.warn("Error calculating position:", error);
      }
    }

    setLinkPosition({
      top: `${popupY}px`,
      left: `${popupX}px`,
    });

    console.log("Setting showLinkInput to true"); // Debug log
    setShowLinkInput(true);
  }, [editor]);

  // Handle heading changes properly
  const handleHeadingChange = useCallback(
    (level: string) => {
      if (!editor) return;

      const levelNum = parseInt(level);
      if (levelNum === 0) {
        editor.chain().focus().setParagraph().run();
      } else {
        editor
          .chain()
          .focus()
          .setHeading({ level: levelNum as 1 | 2 | 3 })
          .run();
      }
    },
    [editor]
  );

  // Get current heading level for dropdown
  const getCurrentHeadingLevel = useCallback(() => {
    if (!editor) return "0";

    if (editor.isActive("heading", { level: 1 })) return "1";
    if (editor.isActive("heading", { level: 2 })) return "2";
    if (editor.isActive("heading", { level: 3 })) return "3";
    return "0";
  }, [editor]);

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content, false);
    }
  }, [content, editor]);

  const handleClickOutside = useCallback((event: any) => {
    if (linkPopupRef?.current && !linkPopupRef?.current?.contains?.(event.target) && !linkButtonRef?.current?.contains?.(event.target)) {
      setShowLinkInput(false);
    }

    if (richTextContainerRef?.current && !richTextContainerRef.current.contains(event.target)) {
      setIsFocused(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  useEffect(() => {
    if (editor && resetFocus) {
      editor.commands.focus();
    }
  }, [resetFocus, editor]);

  const ToolbarButton = ({
    isActive,
    onClick,
    children,
    className = "",
    title,
  }: {
    isActive?: boolean;
    onClick: () => void;
    children: React.ReactNode;
    className?: string;
    title?: string;
  }) => (
    <button
      type="button"
      className={`rich-text-editor-tool-button ${isActive ? "rich-text-editor-active-tool" : ""} ${className}`}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      title={title}
    >
      {children}
    </button>
  );

  const HeadingSelect = ({ isMobile = false }: { isMobile?: boolean }) => (
    <select
      className={`rich-text-editor-heading-select ${isMobile ? "mobile" : ""}`}
      value={getCurrentHeadingLevel()}
      onChange={(e) => handleHeadingChange(e.target.value)}
    >
      <option value="0">Normal</option>
      <option value="1">Heading 1</option>
      <option value="2">Heading 2</option>
      <option value="3">Heading 3</option>
    </select>
  );

  const Toolbar = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className={`rich-text-editor-toolbar ${isMobile ? "mobile" : ""}`}>
      <HeadingSelect isMobile={isMobile} />

      <ToolbarButton isActive={editor?.isActive("bold")} onClick={() => editor?.chain().focus().toggleBold().run()} title="Bold">
        {richTextEditorBold}
      </ToolbarButton>

      <ToolbarButton isActive={editor?.isActive("italic")} onClick={() => editor?.chain().focus().toggleItalic().run()} title="Italic">
        {richTextEditorItalic}
      </ToolbarButton>

      <ToolbarButton isActive={editor?.isActive("underline")} onClick={() => editor?.chain().focus().toggleUnderline().run()} title="Underline">
        {richTextEditorUnderline}
      </ToolbarButton>

      <div className="rich-text-editor-divider" />

      <ToolbarButton isActive={editor?.isActive({ textAlign: "left" })} onClick={() => editor?.chain().focus().setTextAlign("left").run()} title="Align Left">
        {richTextEditorLeftAlign}
      </ToolbarButton>

      <ToolbarButton
        isActive={editor?.isActive({ textAlign: "center" })}
        onClick={() => editor?.chain().focus().setTextAlign("center").run()}
        title="Align Center"
      >
        {richTextEditorCenterAlign}
      </ToolbarButton>

      <ToolbarButton
        isActive={editor?.isActive({ textAlign: "right" })}
        onClick={() => editor?.chain().focus().setTextAlign("right").run()}
        title="Align Right"
      >
        {richTextEditorRightAlign}
      </ToolbarButton>

      <div className="rich-text-editor-divider" />

      <ToolbarButton isActive={editor?.isActive("bulletList")} onClick={() => editor?.chain().focus().toggleBulletList().run()} title="Bullet List">
        {richTextEditorUnorderedList}
      </ToolbarButton>

      <ToolbarButton isActive={editor?.isActive("orderedList")} onClick={() => editor?.chain().focus().toggleOrderedList().run()} title="Numbered List">
        {richTextEditorOrderedList}
      </ToolbarButton>

      <ToolbarButton
        isActive={editor?.isActive("link")}
        onClick={() => {
          showLinkInputBox();
        }}
        className="link-button"
        title="Add Link"
      >
        <span ref={linkButtonRef}>{richTextEditorLink}</span>
      </ToolbarButton>
    </div>
  );

  if (!editor) {
    return (
      <div className="rich-text-editor-loading">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <div ref={richTextContainerRef} className="rich-text-editor-wrapper">
      {/* Always visible toolbars */}
      <div className="rich-text-editor-toolbar-mobile">
        <Toolbar isMobile={true} />
      </div>

      {/* Editor Container */}
      <div ref={inputContainerRef} className={`rich-text-editor-container ${isFocused ? "rich-text-editor-container-focused" : ""}`}>
        <EditorContent editor={editor} className="rich-text-editor" />

        {/* Link Popup */}
        {showLinkInput && (
          <form
            ref={linkPopupRef}
            className="rich-text-editor-link-popup"
            style={{
              position: "absolute",
              top: linkPosition.top,
              left: linkPosition.left,
              zIndex: 1000,
            }}
            onSubmit={saveLink}
          >
            <div className="link-popup-content">
              <label htmlFor="linkInput">Enter URL:</label>
              <input type="url" id="linkInput" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://example.com" autoFocus />
              <div className="link-popup-buttons">
                <button type="submit" className="save-button">
                  Save
                </button>
                <button type="button" className="cancel-button" onClick={() => setShowLinkInput(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
      <div className="rich-text-editor-toolbar-desktop">
        <Toolbar />
      </div>
    </div>
  );
};

const CustomRichTextEditor = ({ richTextContent, handleInputChange, isLoading, resetFocus }: any) => {
  const handleOnChange = useCallback(
    (value: string) => {
      if (!hasContent(value)) {
        handleInputChange("");
        return;
      }
      handleInputChange(value);
    },
    [handleInputChange]
  );

  if (isLoading) {
    return (
      <div className="rich-text-editor-loading">
        <Spinner color="primary" />
      </div>
    );
  }

  return <RichTextEditor content={richTextContent} onChange={handleOnChange} resetFocus={resetFocus} />;
};

export default CustomRichTextEditor;
